import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/pages/About";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { contentApi } from "@/lib/api";
import { Download, FileCheck2, ClipboardList, FolderCheck, ArrowRight, Users, Leaf, Scale, Truck, Settings } from "lucide-react";

const CATEGORY_ICON = {
  labor_human_rights: Users,
  ehs_environment: Leaf,
  business_ethics_anticorruption: Scale,
  responsible_procurement_due_diligence: Truck,
  management_system_improvement: Settings,
};

const STEPS = [
  { icon: ClipboardList, t: "Review the questions", d: "Each checklist lists the assessment questions for that category." },
  { icon: FolderCheck, t: "Gather your evidence", d: "Collect the specific documents and records requested for each requirement." },
  { icon: FileCheck2, t: "Apply with confidence", d: "Start your application knowing exactly what to upload for each of the 47 requirements." },
];

const ChecklistPage = () => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contentApi.checklists().then(({ data }) => setChecklists(data.checklists || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader eyebrow="Process · Prepare" title="Questionnaire & Document Checklist"
        subtitle="Know exactly what to prepare before you begin. Download a preparation checklist for each of the six assessment categories — every one lists the questions you'll answer and the documents you'll need to upload." />

      <section className="wechr-section">
        <div className="wechr-container">
          {/* How it works */}
          <div className="mb-10 grid gap-4 sm:grid-cols-3">
            {STEPS.map((s) => (
              <Card key={s.t}><CardContent className="p-6">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-wgreen-100"><s.icon className="h-5 w-5 text-wgreen-700" /></div>
                <p className="font-heading text-sm font-semibold text-navy-900">{s.t}</p>
                <p className="mt-1 text-sm text-[var(--wechr-muted-text)]">{s.d}</p>
              </CardContent></Card>
            ))}
          </div>

          <h2 className="font-heading text-xl font-semibold text-navy-900">Download the six checklists</h2>
          <p className="mb-6 mt-1 text-sm text-[var(--wechr-muted-text)]">Each PDF covers one assessment category (all categories are weighted equally in your overall score).</p>

          <div className="grid gap-5 md:grid-cols-2">
            {loading
              ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-36 w-full rounded-xl" />)
              : checklists.map((c) => {
                  const Icon = CATEGORY_ICON[c.key] || FileCheck2;
                  return (
                    <Card key={c.key} data-testid={`checklist-card-${c.key}`}>
                      <CardContent className="flex h-full flex-col p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy-900"><Icon className="h-6 w-6 text-white" /></div>
                          <div className="flex-1">
                            <p className="font-heading text-base font-semibold text-navy-900">{c.name}</p>
                            <p className="mt-0.5 text-sm text-[var(--wechr-muted-text)]">{c.requirement_count} requirements · {c.weight}% of overall score</p>
                          </div>
                        </div>
                        <div className="mt-5 flex items-center gap-3">
                          <a href={contentApi.checklistPdfUrl(c.key)} target="_blank" rel="noopener noreferrer" download data-testid={`checklist-download-${c.key}`}>
                            <Button className="btn-green" size="sm"><Download className="mr-2 h-4 w-4" /> Download checklist (PDF)</Button>
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
          </div>

          {/* CTA */}
          <Card className="mt-10 border-[rgba(15,122,74,0.25)] bg-[var(--wechr-subtle)]">
            <CardContent className="flex flex-col items-start justify-between gap-4 p-6 sm:flex-row sm:items-center">
              <div>
                <p className="font-heading text-base font-semibold text-navy-900">Ready when you are</p>
                <p className="mt-1 text-sm text-[var(--wechr-muted-text)]">Once your documents are prepared, start your certification application. You can also review the full assessment criteria first.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to="/assessment-criteria"><Button variant="outline">View criteria</Button></Link>
                <Link to="/apply"><Button className="btn-green">Start application <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ChecklistPage;
